import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { StructureViewer, type ViewerStats } from './components/StructureViewer';
import { blockLabel } from './nbt/blockLabel';
import { parseStructureNbt } from './nbt/parseStructure';
import type { StructureModel } from './nbt/types';
import type { RenderMode } from './render/buildGeometry';
import { type ResourcePackTextures } from './render/structureAssets';
import { blockAppearance } from './textures/blockAppearance';
import { blockDataUrl, materialDataUrl } from './textures/preview';
import { readResourcePack, type RawResourcePack } from './textures/resourcePack';
import { createShareLink, fetchSharedStructure, sharedIdFromLocation, shareUrlFor, type ShareLink } from './share';
import type { LessonGuide } from '../shared/guide';
import { fetchGuide } from './guide/api';
import { statesForBlockIds } from './guide/match';
import { clearProgress, loadProgress, saveProgress } from './guide/progress';
import { RubyText } from './guide/ruby';
import { GuidePanel } from './components/GuidePanel';
import { InventoryHint } from './components/InventoryHint';
import { InventoryMapButton } from './components/InventoryMap';
import { MaterialCatalog } from './components/MaterialCatalog';
import { prepareEducationAssets } from './textures/educationPack';

type LoadState = 'loading' | 'ready' | 'error';

/** ビューワーと設定を並べて置ける幅か。CSSの分岐と同じ値にしている。 */
const WIDE_SCREEN = typeof window === 'undefined'
  ? { matches: true, addEventListener() {}, removeEventListener() {} } as unknown as MediaQueryList
  : window.matchMedia('(min-width: 1000px)');

export function App() {
  const [model, setModel] = useState<StructureModel | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [message, setMessage] = useState('サンプル建築を読み込んでいます…');
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [mode, setMode] = useState<RenderMode>('texture');
  const [rawPack, setRawPack] = useState<RawResourcePack | null>(null);
  const [pack, setPack] = useState<ResourcePackTextures | null>(null);
  const [packMessage, setPackMessage] = useState<string | null>(null);
  const [stats, setStats] = useState<ViewerStats | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [panelOpen, setPanelOpen] = useState(() => WIDE_SCREEN.matches);
  const [sourceBuffer, setSourceBuffer] = useState<ArrayBuffer | null>(null);
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [guide, setGuide] = useState<LessonGuide | null>(null);
  /** 進捗保存のキー。共有IDか、同梱サンプルの'example'。 */
  const [guideShareId, setGuideShareId] = useState<string | null>(null);
  const [guideStep, setGuideStep] = useState(0);
  const [guideMax, setGuideMax] = useState(0);
  const [guideChecks, setGuideChecks] = useState<Record<string, boolean>>({});
  /** かんせいをみる: ONの間はハイライトの減光を外して完成形をフルカラーで見せる。 */
  const [showComplete, setShowComplete] = useState(false);
  /** じゅんび画面で最後にタップしたブロック。3Dで「これのことだよ」を見せる。 */
  const [itemPreview, setItemPreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const packInputRef = useRef<HTMLInputElement>(null);

  // 広い画面では並べて表示、狭い画面では閉じたドロワーを既定にする。
  useEffect(() => {
    const sync = (event: MediaQueryListEvent) => setPanelOpen(event.matches);
    WIDE_SCREEN.addEventListener('change', sync);
    return () => WIDE_SCREEN.removeEventListener('change', sync);
  }, []);

  /** 建築で使われているブロックを、多い順に並べた一覧。 */
  const blockList = useMemo(() => {
    if (!model) return [];
    // 向きや信号の強さが違うだけのものは、同じブロックとして1行にまとめる。
    const groups = new Map<string, { state: number; count: number }>();
    model.blocks.forEach((block) => {
      const label = blockLabel(model.palette[block.state].name);
      const group = groups.get(label);
      if (group) group.count += 1;
      else groups.set(label, { state: block.state, count: 1 });
    });
    return [...groups.entries()]
      .map(([label, { state, count }]) => {
        const entry = model.palette[state];
        return {
          state,
          entry,
          count,
          label,
          texture: blockAppearance(entry.name, entry.properties).faces[0],
        };
      })
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
  }, [model, pack]);

  const blockThumbnails = useMemo(() => {
    const urls = new Map<string, string>();
    blockList.forEach((item) => {
      if (urls.has(item.entry.name)) return;
      const url = blockDataUrl(item.entry.name, pack?.textures, pack?.previews?.get(item.entry.name));
      if (url) urls.set(item.entry.name, url);
    });
    return urls;
  }, [blockList, pack]);

  const selectBlockKind = useCallback((state: number) => {
    if (!model) return;
    const index = model.blocks.findIndex((block) => block.state === state);
    if (index < 0) return;
    setSelected(index);
    // 狭い画面ではドロワーが建築を覆うので、選んだら閉じて見せる。
    if (!WIDE_SCREEN.matches) setPanelOpen(false);
  }, [model]);

  const selection = useMemo(() => {
    if (!model || selected === null) return null;
    const block = model.blocks[selected];
    if (!block) return null;
    const entry = model.palette[block.state];
    const label = blockLabel(entry.name);
    return {
      block,
      entry,
      label,
      // 一覧と同じく、向き違いも同じブロックとして数える。
      count: model.blocks.reduce(
        (total, item) => (blockLabel(model.palette[item.state].name) === label ? total + 1 : total),
        0,
      ),
    };
  }, [model, selected, pack]);

  /** 解析して表示する。呼び出し側が後続処理を続けてよいかを戻り値で伝える。 */
  const loadBuffer = useCallback(async (buffer: ArrayBuffer, name: string): Promise<boolean> => {
    setLoadState('loading');
    setMessage(`${name}を解析しています…`);
    try {
      const structure = await parseStructureNbt(buffer, name);
      setModel(structure);
      setSourceBuffer(buffer);
      setLoadState('ready');
      setMessage(`${structure.blocks.length.toLocaleString()}ブロックを読み込みました。`);
      return true;
    } catch (error) {
      setLoadState('error');
      setMessage(error instanceof Error ? error.message : 'NBTを読み込めませんでした。');
      return false;
    }
  }, []);

  /** 別の建築に切り替えたら、前の共有リンク・アドレス・ガイドは持ち越さない。 */
  const forgetShare = useCallback(() => {
    setShareLink(null);
    setShareMessage(null);
    setGuide(null);
    setGuideShareId(null);
    setGuideStep(0);
    setGuideMax(0);
    setGuideChecks({});
    setShowComplete(false);
    setItemPreview(null);
    if (sharedIdFromLocation(window.location.pathname)) {
      window.history.replaceState(null, '', '/');
    }
  }, []);

  /** ガイドモードを開始する。前回の進捗が保存されていれば、その場所から再開する。 */
  const startGuide = useCallback((lesson: LessonGuide, shareId: string) => {
    // ガイドを別レッスンへ差し替えたとき、前のレッスンの進捗を引き継がないよう
    // 保存キーにはレッスンの識別子も混ぜる。
    const storageId = `${shareId}:${lesson.lessonId ?? lesson.title}`;
    const restored = loadProgress(storageId, lesson.steps.length);
    // アイテム集めの無いレッスンは、じゅんび画面(0)を飛ばしてSTEP 1から。
    const firstStep = lesson.items.length > 0 ? 0 : 1;
    setGuide(lesson);
    setGuideShareId(storageId);
    setGuideStep(Math.max(firstStep, restored?.step ?? firstStep));
    setGuideMax(Math.max(firstStep, restored?.maxVisited ?? firstStep));
    setGuideChecks(restored?.items ?? {});
    setShowComplete(false);
    setItemPreview(null);
    setSelected(null);
    // ガイドが主役なので、TOOLSは広い画面でも閉じたドロワーから始める。
    setPanelOpen(false);
  }, []);

  const loadSample = useCallback(async (edition: 'java' | 'bedrock' | 'catalog' = 'java') => {
    const fileName = edition === 'catalog' ? 'education-blocks.mcstructure' : edition === 'java' ? 'tatami-house.nbt' : 'tatami-house.mcstructure';
    forgetShare();
    setLoadState('loading');
    setMessage('サンプル建築を読み込んでいます…');
    try {
      const response = await fetch(`/examples/${fileName}`);
      if (!response.ok) throw new Error('サンプルファイルを取得できませんでした。');
      await loadBuffer(await response.arrayBuffer(), fileName);
    } catch (error) {
      setLoadState('error');
      setMessage(error instanceof Error ? error.message : 'サンプルを読み込めませんでした。');
    }
  }, [loadBuffer, forgetShare]);

  const loadShared = useCallback(async (id: string) => {
    setLoadState('loading');
    setMessage('共有された建築を読み込んでいます…');
    try {
      // ガイドの取得は裏で走らせ、建築本体の表示を待たせない。
      const guidePromise = fetchGuide(`/api/share/${id}/guide`);
      const shared = await fetchSharedStructure(id);
      if (!(await loadBuffer(shared.buffer, shared.name))) return;
      setShareLink({ id, url: shareUrlFor(id) });
      // ガイドが付いた共有なら、開くだけで自動的にレッスンガイドモードになる。
      const lesson = await guidePromise;
      if (lesson) startGuide(lesson, id);
    } catch (error) {
      setLoadState('error');
      setMessage(error instanceof Error ? error.message : '共有された建築を読み込めませんでした。');
    }
  }, [loadBuffer, startGuide]);

  /** ローカル開発・動作確認用: サンプル建築＋同梱ガイドでガイドモードを試す。 */
  const loadExampleGuide = useCallback(async () => {
    await loadSample('bedrock');
    const lesson = await fetchGuide('/examples/tatami-house.guide.json');
    if (lesson) startGuide(lesson, 'example');
  }, [loadSample, startGuide]);

  useEffect(() => {
    const shared = sharedIdFromLocation(window.location.pathname);
    if (shared) void loadShared(shared);
    else if (new URLSearchParams(window.location.search).get('guide') === 'example') void loadExampleGuide();
    else void loadSample();
    // 初回の読み込み先だけを決める。以後の切り替えはボタンとファイル選択で行う。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelected(null);
  }, [model]);

  useEffect(() => {
    if (!model) return;
    let cancelled = false;
    setPack(null);
    setPackMessage('マイクラの テクスチャを 読み込んでいます…');
    void (async () => {
      try {
        const entries = [...model.palette, ...model.palette.map(({ name }) => ({ name, properties: {} }))];
        const materials = guide ? [...guide.items, ...guide.steps.flatMap(step => step.items ?? [])].map(item => item.block) : [];
        const resolved = await prepareEducationAssets(entries, rawPack, materials);
        if (cancelled) return;
        setStats(null);
        setPack(resolved);
        const missing = resolved.unsupported?.length ?? 0;
        setPackMessage(`${rawPack ? rawPack.name + ' の画像を適用中。' : ''}教育版1.26.32・${resolved.catalogSize}件の定義を収録。${missing ? `未対応: ${resolved.unsupported?.join('、')}` : 'この建築の全ブロックを読み込みました。'}`);
      } catch (error) {
        if (!cancelled) setPackMessage(error instanceof Error ? error.message : '見た目データを読み込めませんでした。');
      }
    })();
    return () => { cancelled = true; };
  }, [model, rawPack, guide]);

  const loadFile = useCallback(async (file?: File) => {
    if (!file) return;
    if (!/\.(nbt|mcstructure)$/i.test(file.name)) {
      setLoadState('error');
      setMessage('.nbt（Java版）または.mcstructure（教育版・統合版）を選んでください。');
      return;
    }
    forgetShare();
    await loadBuffer(await file.arrayBuffer(), file.name);
  }, [loadBuffer, forgetShare]);

  const share = useCallback(async () => {
    if (!sourceBuffer || !model) return;
    setSharing(true);
    setShareMessage(null);
    try {
      const link = await createShareLink(sourceBuffer, model.name);
      setShareLink(link);
      window.history.replaceState(null, '', `/s/${link.id}`);
      setShareMessage('リンクを作りました。このリンクを知っている人だけが開けます。');
    } catch (error) {
      setShareMessage(error instanceof Error ? error.message : '共有リンクを作れませんでした。');
    } finally {
      setSharing(false);
    }
  }, [sourceBuffer, model]);

  const copyShareLink = useCallback(async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink.url);
      setShareMessage('リンクをコピーしました。');
    } catch {
      setShareMessage('コピーできませんでした。上のリンクを選んでコピーしてください。');
    }
  }, [shareLink]);

  const loadPack = useCallback(async (file?: File) => {
    if (!file) return;
    setPackMessage(`${file.name} を読み込んでいます…`);
    try {
      setRawPack(await readResourcePack(file));
      setMode('texture');
    } catch (error) {
      setRawPack(null);
      setPackMessage(error instanceof Error ? error.message : 'リソースパックを読み込めませんでした。');
    }
  }, []);

  const clearPack = useCallback(() => {
    setRawPack(null);
    setPackMessage(null);
    if (packInputRef.current) packInputRef.current.value = '';
  }, []);

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    void loadFile(event.dataTransfer.files[0]);
  };

  const navigateGuide = useCallback((target: number) => {
    if (!guide) return;
    const first = guide.items.length > 0 ? 0 : 1;
    const step = Math.max(first, Math.min(guide.steps.length + 1, target));
    setGuideStep(step);
    setGuideMax((max) => Math.max(max, step));
    setShowComplete(false);
    setItemPreview(null);
  }, [guide]);

  const toggleGuideCheck = useCallback((block: string) => {
    setGuideChecks((checks) => ({ ...checks, [block]: checks[block] !== true }));
    setItemPreview(block);
  }, []);

  const resetGuide = useCallback(() => {
    if (guideShareId) clearProgress(guideShareId);
    // アイテム集めの無いレッスンは、じゅんび画面(0)を飛ばしてSTEP 1から。
    const first = guide && guide.items.length > 0 ? 0 : 1;
    setGuideStep(first);
    setGuideMax(first);
    setGuideChecks({});
    setShowComplete(false);
    setItemPreview(null);
  }, [guide, guideShareId]);

  /** かんせい画面の「じゆうに みる」。建築はそのままに、通常のビューアへ戻る。 */
  const exitGuide = useCallback(() => {
    setGuide(null);
    setGuideShareId(null);
    setShowComplete(false);
    setItemPreview(null);
    setPanelOpen(WIDE_SCREEN.matches);
  }, []);

  // 進捗はlocalStorageへ書き込み、リロードしても同じ場所から再開できるようにする。
  useEffect(() => {
    if (!guide || !guideShareId) return;
    saveProgress(guideShareId, { step: guideStep, maxVisited: guideMax, items: guideChecks });
  }, [guide, guideShareId, guideStep, guideMax, guideChecks]);

  /** ガイドのアイテム用サムネイル。ブロックID → data URL。 */
  const guideThumbnails = useMemo(() => {
    const urls = new Map<string, string>();
    if (!guide) return urls;
    const items = [...guide.items, ...guide.steps.flatMap((step) => step.items ?? [])];
    items.forEach(({ block }) => {
      if (urls.has(block)) return;
      const url = materialDataUrl(block, pack);
      if (url) urls.set(block, url);
    });
    return urls;
  }, [guide, pack]);

  /** いまのステップで3Dが注目させるブロックのstate集合。nullなら通常表示。 */
  const guideHighlight = useMemo(() => {
    if (!model || !guide || showComplete) return null;
    if (guideStep === 0) {
      if (!itemPreview) return null;
      const states = statesForBlockIds(model, [itemPreview]);
      return states.size > 0 ? states : null;
    }
    const step = guide.steps[guideStep - 1];
    if (!step?.highlight) return null;
    const states = statesForBlockIds(model, step.highlight);
    if (states.size === 0) {
      // ガイドの書き間違い（IDのずれ）に作成者が気づけるように残す。子ども向けの画面は通常表示のまま。
      console.warn(`ガイドのhighlightがモデルに見つかりません: ${step.highlight.join(', ')}`);
      return null;
    }
    return states;
  }, [model, guide, guideStep, showComplete, itemPreview]);

  return (
    <main className={`app-shell${guide ? ' guide-mode' : ''}`}>
      <header className="masthead">
        <div className="masthead-text">
          <p className="eyebrow">{guide ? 'TATAMI STUDY · LESSON' : 'TATAMI STUDY · BUILD PREVIEW'}</p>
          <h1>{guide ? <RubyText text={guide.title} /> : 'NBT Viewer'}</h1>
          <p className="lede">構造ブロックから書き出した建築を、ブラウザですぐ立体確認。</p>
        </div>
        <button
          type="button"
          className="panel-toggle"
          aria-expanded={panelOpen}
          aria-controls="tools-panel"
          onClick={() => setPanelOpen((open) => !open)}
        >
          {panelOpen ? '設定を閉じる' : '設定をひらく'}
        </button>
      </header>

      <section className={`workspace${panelOpen ? ' panel-open' : ''}${guide ? ' guide-open' : ''}`} aria-live="polite">
        <div className="stage-panel">
          {model
            ? (
              <StructureViewer
                model={model}
                mode={mode}
                pack={pack}
                selected={selected}
                highlight={guideHighlight}
                autoRotate={guide !== null && guideStep > guide.steps.length}
                onError={setViewerError}
                onSelect={setSelected}
                onStats={setStats}
              />
            )
            : <div className="viewer-placeholder" />}
          {guide && (guideHighlight !== null || showComplete) && (
            <button
              type="button"
              className={`complete-toggle${showComplete ? ' is-active' : ''}`}
              aria-pressed={showComplete}
              onClick={() => setShowComplete((value) => !value)}
            >
              {showComplete ? '🔦 いまの ブロックを みる' : '👁 かんせいを みる'}
            </button>
          )}
          {mode === 'texture' && packMessage && (
            <p className="texture-status" role="status">
              {!pack ? packMessage
                : stats && stats.sourced < stats.textures ? `一部は簡易表示です（実物テクスチャ ${stats.sourced}/${stats.textures}枚）。設定からリソースパックも使えます。`
                  : pack.unsupported?.length ? `未対応のブロックがあります：${pack.unsupported.map(blockLabel).join('、')}` : '教育版のブロックモデルで表示中'}
            </p>
          )}
          {(loadState !== 'ready' || !selection) && (
            <div className={`status-card status-float status-${loadState}`}>
              <span className="status-dot" />
              <p>{message}</p>
            </div>
          )}

          {selection && (
            <div className="selection-card selection-float">
              <div className="selection-head">
                <div className="selection-title">
                  <p className="details-label">SELECTED BLOCK</p>
                  <h2 title={selection.entry.name}>{selection.label}</h2>
                </div>
                <button type="button" className="selection-close" aria-label="選択を解除" onClick={() => setSelected(null)}>
                  ×
                </button>
              </div>
              <p className="selection-id">{selection.entry.name}</p>
              <InventoryHint block={selection.entry.name} />
              <InventoryMapButton block={selection.entry.name} />
              <dl>
                <div><dt>座標</dt><dd>{selection.block.position.join(', ')}</dd></div>
                <div><dt>使用数</dt><dd>{selection.count.toLocaleString()}</dd></div>
              </dl>
              {Object.keys(selection.entry.properties).length > 0 && (
                <ul className="selection-properties">
                  {Object.entries(selection.entry.properties).map(([property, value]) => (
                    <li key={property}><span>{property}</span>{value}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="control-hint">
            <span>ドラッグ</span> 回転　<span>ホイール / ピンチ</span> 拡大縮小　<span>2本指 / 右ドラッグ</span> 移動　<span>タップ</span> 調べる
          </div>
          {loadState === 'loading' && <div className="loading-overlay"><span className="spinner" />解析中</div>}
          {viewerError && <div className="error-overlay">{viewerError}</div>}
        </div>

        <button
          type="button"
          className="panel-scrim"
          tabIndex={-1}
          aria-label="設定を閉じる"
          onClick={() => setPanelOpen(false)}
        />

        <aside className="side-panel" id="tools-panel">
          <div className="panel-head">
            <p className="details-label">TOOLS</p>
            <button type="button" className="panel-close" aria-label="設定を閉じる" onClick={() => setPanelOpen(false)}>
              ×
            </button>
          </div>

          <div
            className={`drop-zone${dragging ? ' is-dragging' : ''}`}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".nbt,.mcstructure,application/octet-stream"
              onChange={(event) => void loadFile(event.target.files?.[0])}
              hidden
            />
            <div className="upload-mark" aria-hidden="true">＋</div>
            <h2>建築をひらく</h2>
            <p>ここへドロップするか、ファイルを選択</p>
            <button type="button" className="upload-button" onClick={() => inputRef.current?.click()}>
              ファイルを選ぶ
            </button>
            <small>Java版 .nbt / 教育版・統合版 .mcstructure / 最大16MB</small>
          </div>

          <div className="sample-buttons">
            <button className="sample-button" type="button" onClick={() => void loadSample('java')}>
              Java版サンプル
            </button>
            <button className="sample-button" type="button" onClick={() => void loadSample('bedrock')}>
              教育版サンプル
            </button>
            <button className="sample-button catalog-sample" type="button" onClick={() => void loadSample('catalog')}>
              教育版の全ブロックを確認
            </button>
          </div>

          <div className="options-card">
            <p className="details-label">APPEARANCE</p>
            <div className="mode-switch" role="group" aria-label="表示モード">
              <button
                type="button"
                className={mode === 'texture' ? 'is-active' : ''}
                aria-pressed={mode === 'texture'}
                onClick={() => setMode('texture')}
              >
                テクスチャ
              </button>
              <button
                type="button"
                className={mode === 'color' ? 'is-active' : ''}
                aria-pressed={mode === 'color'}
                onClick={() => setMode('color')}
              >
                カラー
              </button>
            </div>

            <input
              ref={packInputRef}
              type="file"
              accept=".zip,.jar,application/zip,application/java-archive"
              onChange={(event) => void loadPack(event.target.files?.[0])}
              hidden
            />
            <div className="pack-row">
              <button type="button" className="pack-button" onClick={() => packInputRef.current?.click()}>
                リソースパックを使う
              </button>
              {rawPack && (
                <button type="button" className="pack-clear" onClick={clearPack}>
                  解除
                </button>
              )}
            </div>
            <small>
              {packMessage ?? '教育版1.26.32のブロックデータを使います。手元の.zip / .jarで画像を差し替えることもできます。'}
            </small>
          </div>

          <div className="options-card">
            <p className="details-label">SHARE</p>
            {shareLink && (
              <input
                className="share-url"
                readOnly
                value={shareLink.url}
                aria-label="共有リンク"
                onFocus={(event) => event.currentTarget.select()}
              />
            )}
            <div className="pack-row">
              <button type="button" className="pack-button" onClick={() => void share()} disabled={!model || sharing}>
                {sharing ? '作成中…' : shareLink ? 'もう一度作る' : '共有リンクを作る'}
              </button>
              {shareLink && (
                <button type="button" className="pack-clear share-copy" onClick={() => void copyShareLink()}>
                  コピー
                </button>
              )}
            </div>
            <small>
              {shareMessage ?? 'リンクを開くだけで、この建築を誰でも立体で見られます。ログインは要りません。'}
            </small>
          </div>

          {model && (
            <div className="details-card structure-card">
              <p className="details-label">CURRENT STRUCTURE</p>
              <h2 title={model.name}>{model.name}</h2>
              <dl>
                <div><dt>サイズ</dt><dd>{model.size.join(' × ')}</dd></div>
                <div><dt>表示</dt><dd>{model.blocks.length.toLocaleString()} blocks</dd></div>
                <div><dt>種類</dt><dd>{new Set(model.blocks.map((block) => block.state)).size}</dd></div>
                <div><dt>描画面</dt><dd>{(stats?.faces ?? 0).toLocaleString()}</dd></div>
                <div><dt>形式</dt><dd>{model.format}-endian</dd></div>
                <div><dt>テクスチャ</dt><dd>{stats ? `${stats.sourced}/${stats.textures}` : '—'}</dd></div>
              </dl>
            </div>
          )}

          <MaterialCatalog rawPack={rawPack} />

          {blockList.length > 0 && (
            <details className="block-list">
              <summary>使われているブロック（{blockList.length}種）</summary>
              <ul>
                {blockList.map((item) => (
                  <li key={item.state}>
                    <button type="button" onClick={() => selectBlockKind(item.state)} title={item.entry.label}>
                      {blockThumbnails.has(item.entry.name)
                        ? <img src={blockThumbnails.get(item.entry.name)} alt="" width={22} height={22} />
                        : <span className="block-swatch" style={{ background: `#${item.entry.color.toString(16).padStart(6, '0')}` }} />}
                      <span className="block-name">{item.label}</span>
                      <span className="block-count">{item.count.toLocaleString()}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <details className="howto">
            <summary>Java版：.nbtの作り方</summary>
            <ol>
              <li>クリエイティブ（チート有効）で <code>/give @s structure_block</code> を実行する</li>
              <li>ストラクチャーブロックを置いて右クリックし、左上のボタンで<b>「保存」</b>モードにする</li>
              <li>構造物の名前を入れる（例: <code>my_house</code>）</li>
              <li>「相対位置」と「サイズ」で建物を囲む。<b>「表示枠を表示」</b>をONにすると範囲が見える</li>
              <li><b>「保存」</b>を押す</li>
              <li><code>.minecraft/saves/&lt;ワールド名&gt;/generated/minecraft/structures/&lt;名前&gt;.nbt</code> をこのページにドロップ</li>
            </ol>
            <small>一度に保存できるのは48×48×48まで。大きい建築は分けて保存します。</small>
          </details>

          <details className="howto">
            <summary>教育版・統合版：.mcstructureの作り方</summary>
            <ol>
              <li>クリエイティブで <code>/give @s structure_block</code> を実行する</li>
              <li>置いて右クリックし、<b>「保存」</b>モードで名前とサイズを決める</li>
              <li><b>「エクスポート」</b>を押すと <code>.mcstructure</code> が書き出される</li>
              <li>教育版(Windows)の保存先は <code>%AppData%\Minecraft Education Edition\games\com.mojang\</code> 以下</li>
              <li>そのファイルをこのページにドロップ</li>
            </ol>
            <small>水中の建築など、統合版が第2レイヤーに持つ水は表示しません。元素や化学実験セットも教育版の画像で表示します。看板の文字や旗の追加模様は表示しません。</small>
          </details>

          <p className="privacy-note">ファイルはサーバーへ送信せず、このブラウザ内だけで解析します。</p>
        </aside>

        {guide && (
          <GuidePanel
            guide={guide}
            step={guideStep}
            maxVisited={guideMax}
            checks={guideChecks}
            thumbnails={guideThumbnails}
            onNavigate={navigateGuide}
            onToggleCheck={toggleGuideCheck}
            onReset={resetGuide}
            onExit={exitGuide}
          />
        )}
      </section>
    </main>
  );
}
